import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Switch} from '@/web-components';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import {HorizontalFlexWrapper} from '../salesforce/app_salesforce_style';
import {SelectionElementLabel} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import {createToast} from '../../../shared/components/toasts/toast';
import {useIntl} from 'react-intl';
import DirectApi from '../../../../directApi';

export const HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES = ['dealName', 'dealAmount', 'dealDates'];

const HUBSPOT_SYNC_DIRECTION_LABEL_IDS = {
	dealName: 'settings.app_catalog.hubspot.sync_direction.deal_name',
	dealAmount: 'settings.app_catalog.hubspot.sync_direction.deal_amount',
	dealDates: 'settings.app_catalog.hubspot.sync_direction.deal_dates',
};

const HUBSPOT_LINKED_FIELD_LINKED_BY_DEFAULT = new Set(['dealName', 'dealAmount', 'dealDates']);

const defaultHubspotLinkedField = (intl, key) => ({
	linked: HUBSPOT_LINKED_FIELD_LINKED_BY_DEFAULT.has(key),
	text: intl.formatMessage({id: HUBSPOT_SYNC_DIRECTION_LABEL_IDS[key]}),
});

export function buildHubspotLinkedFieldsPutBody(linkedFields, explicitUnlinkedAsNull = false) {
	const body = {};
	for (const fieldKey of Object.keys(linkedFields)) {
		if (explicitUnlinkedAsNull) {
			body[fieldKey] = linkedFields[fieldKey].linked ? linkedFields[fieldKey].text : null;
		} else if (linkedFields[fieldKey].linked) {
			body[fieldKey] = linkedFields[fieldKey].text;
		}
	}
	return body;
}

export function normalizeHubspotLinkedFields(raw, intl) {
	const merged = {...(raw || {})};

	for (const key of HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES) {
		const fallback = defaultHubspotLinkedField(intl, key);
		if (merged[key] == null) {
			merged[key] = {...fallback};
		} else {
			const existing = merged[key];
			merged[key] = {
				...existing,
				text: existing.text || fallback.text,
				linked: existing.linked === true,
			};
		}
	}

	const ordered = {};
	for (const key of HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES) {
		if (merged[key] != null) {
			ordered[key] = merged[key];
		}
	}
	for (const key of Object.keys(merged)) {
		if (ordered[key] === undefined) {
			ordered[key] = merged[key];
		}
	}
	return ordered;
}

const genericIntegrationLinkedFields = ({integrationName, onHubspotLinkedFieldsUpdated, reloadTrigger = 0}) => {
	const intl = useIntl();
	const [linkedFields, setLinkedFields] = useState(null);
	const onHubspotLinkedFieldsUpdatedRef = useRef(onHubspotLinkedFieldsUpdated);
	onHubspotLinkedFieldsUpdatedRef.current = onHubspotLinkedFieldsUpdated;

	const loadLinkedFields = useCallback(() => {
		DirectApi.Fetch(`${integrationName}/linked_fields`).then(json => {
			let fields = json.linkedFields;
			if (integrationName === 'hubspot') {
				fields = normalizeHubspotLinkedFields(fields ?? {}, intl);
			}
			setLinkedFields(fields);
			if (integrationName === 'hubspot') {
				onHubspotLinkedFieldsUpdatedRef.current?.(fields);
			}
		});
	}, [integrationName, intl]);

	useEffect(() => {
		loadLinkedFields();
	}, [loadLinkedFields, reloadTrigger]);

	const handleSwitchChange = (value, field) => {
		const newLinkedFields = {...linkedFields};
		newLinkedFields[field].linked = !value;

		const body = buildHubspotLinkedFieldsPutBody(newLinkedFields, integrationName === 'hubspot');

		const headers = new Headers();
		headers.append('Content-Type', 'application/json');

		const init = {
			headers: headers,
			credentials: 'include',
			method: 'PUT',
			body: JSON.stringify(body),
		};

		return fetch(DirectApi.graphqlServerEndpoint(`hubspot/linked_fields`), init).then(() => {
			setLinkedFields(newLinkedFields);
			onHubspotLinkedFieldsUpdatedRef.current?.(newLinkedFields);
			createToast({
				duration: 2000,
				message: intl.formatMessage({id: 'common.changes_saved'}),
			});
		});
	};

	const hubspotRenderKeys = integrationName === 'hubspot' ? HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES : null;

	return linkedFields ? (
		<>
			{(hubspotRenderKeys || Object.keys(linkedFields)).map(field =>
				linkedFields[field] ? (
					<LinkedFieldsWrapper key={field}>
						<HorizontalFlexWrapper>
							<Switch
								checked={linkedFields[field].linked}
								onChange={value => handleSwitchChange(value, field)}
								cy={integrationName === 'hubspot' ? `hubspot-linked-field-${field}` : undefined}
							/>
							<SelectionElementLabel>{linkedFields[field].text}</SelectionElementLabel>
						</HorizontalFlexWrapper>
					</LinkedFieldsWrapper>
				) : null
			)}
		</>
	) : (
		<InlineLoader />
	);
};

const LinkedFieldsWrapper = styled.div`
	margin: 8px 0;
`;

export default genericIntegrationLinkedFields;
